import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { SwooshRibbons } from "@/components/SwooshRibbons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#1C1512]/15 bg-white px-5 py-4 text-base text-[#1C1512] placeholder-[#A79C90] outline-none transition-colors duration-300 focus:border-ruby/60 focus:ring-1 focus:ring-ruby/40";

const labelCls =
  "mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B615A]";

export const Enquiry = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquire" data-testid="enquiry-section" className="relative overflow-hidden border-t border-white/5 bg-[#0B0A09] py-24 sm:py-32">
      <SwooshRibbons
        idPrefix="enquiry-tr"
        flip
        inView
        className="absolute -right-20 -top-10 h-[320px] w-[500px] opacity-75"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-16 px-4 sm:px-8 lg:grid-cols-2 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="overline-tag mb-5">Enquire</p>
          <h2 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl lg:text-5xl lg:leading-[1.1]">
            Ready to add sales capability{" "}
            <span className="font-display italic text-blush">to your team?</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
            Fill in your details below to schedule an initial commercial growth strategy
            conversation. No obligation, no junior handoffs — just a direct talk about your
            pipeline.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="rounded-2xl border-2 border-blush/60 bg-[#F7F1E9] p-8 shadow-[0_0_80px_rgba(229,72,77,0.25),0_30px_60px_rgba(0,0,0,0.45)] sm:p-10"
        >
          <AnimatePresence mode="wait">
            {sent ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="flex min-h-[22rem] flex-col items-center justify-center text-center"
                data-testid="enquiry-success"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full border border-ruby/40 bg-ruby/10">
                  <CheckCircle2 className="h-8 w-8 text-ruby" />
                </span>
                <h3 className="mt-6 font-heading text-2xl font-semibold text-[#1C1512]">
                  Enquiry received
                </h3>
                <p className="mt-3 max-w-xs text-base text-[#5C5248]">
                  Thanks, {form.name.split(" ")[0]}. Your message is in — expect a direct reply
                  shortly to book your strategy conversation.
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                exit={{ opacity: 0, y: -12 }}
                onSubmit={submit}
                className="space-y-5"
                data-testid="enquiry-form"
              >
                <div>
                  <label htmlFor="enquiry-name" className={labelCls}>
                    Full Name
                  </label>
                  <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Citizen" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className={labelCls}>
                    Email Address
                  </label>
                  <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com.au" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-message" className={labelCls}>
                    Message / Business Growth Goals
                  </label>
                  <textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Tell me about your pipeline, your team, and where you want to be in 12 months." className={`${inputCls} resize-none`} />
                </div>
                <button
                  type="submit"
                  data-testid="enquiry-submit-button"
                  disabled={sending}
                  className="btn-magnetic group flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E5484D] to-[#C8102E] px-8 py-4 font-heading text-base font-semibold text-white disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Send Enquiry"}
                  {!sending && (
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
